import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

const scrollTo = (id, after) => (e) => {
  e.preventDefault();
  after?.();
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const MobileMenu = ({ open, onClose, landing }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        key="menu"
        data-testid="mobile-menu"
        initial={{ opacity: 0, y: -8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="md:hidden mt-2 rounded-2xl border border-white/10 bg-[#2b3a54]/95 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-2 flex flex-col text-base font-medium text-slate-200"
      >
        {landing && (
          <a href="#how-it-works" onClick={scrollTo("how-it-works", onClose)} data-testid="mobile-link-how-it-works" className="px-4 py-3 rounded-xl hover:bg-white/5 hover:text-[var(--clinical)] transition-colors">
            How it works
          </a>
        )}
        <Link to="/privacy" onClick={onClose} data-testid="mobile-link-privacy" className="px-4 py-3 rounded-xl hover:bg-white/5 hover:text-[var(--clinical)] transition-colors">
          Privacy Policy
        </Link>
        <Link to="/terms" onClick={onClose} data-testid="mobile-link-terms" className="px-4 py-3 rounded-xl hover:bg-white/5 hover:text-[var(--clinical)] transition-colors">
          Terms of Service
        </Link>
        <span className="mx-4 my-2 h-px bg-white/10" />
        <span className="px-4 pb-2 pt-1 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[var(--clinical)]">
          <span className="relative flex h-2 w-2 pulse-ring">
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--green)]" />
          </span>
          Internal testing
        </span>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Nav = ({ landing = false }) => {
  const [open, setOpen] = useState(false);
  return (
  <motion.header
    initial={{ y: -24, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
    className="fixed top-0 inset-x-0 z-50"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4">
      <nav className="flex items-center justify-between rounded-full border border-white/10 bg-[#2b3a54]/70 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.35)] pl-4 pr-2 py-2">
        <Link to="/" data-testid="nav-beaconshield-logo" aria-label="BeaconShield home" onClick={() => setOpen(false)}>
          <Logo size={30} />
        </Link>
        <div className="hidden md:flex items-center gap-1 text-sm font-medium text-[#e3e8f0]">
          {landing && (
            <a href="#how-it-works" onClick={scrollTo("how-it-works")} data-testid="nav-link-how-it-works" className="px-4 py-2 rounded-full hover:bg-white/5 hover:text-[var(--clinical)] transition-colors">
              How it works
            </a>
          )}
          <Link to="/privacy" data-testid="nav-link-privacy" className="px-4 py-2 rounded-full hover:bg-white/5 hover:text-[var(--clinical)] transition-colors">
            Privacy
          </Link>
          <Link to="/terms" data-testid="nav-link-terms" className="px-4 py-2 rounded-full hover:bg-white/5 hover:text-[var(--clinical)] transition-colors">
            Terms
          </Link>
        </div>
        <span data-testid="nav-status-badge" className="hidden md:inline-flex items-center gap-2 rounded-full border border-[var(--clinical)]/30 bg-[var(--clinical)]/10 text-[var(--clinical)] text-xs font-mono tracking-wider uppercase px-3.5 py-2">
          <span className="relative flex h-2 w-2 pulse-ring">
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--green)]" />
          </span>
          Internal testing
        </span>
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-200 hover:bg-white/5 hover:text-[var(--clinical)] transition-colors"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>
      <div id="mobile-menu">
        <MobileMenu open={open} onClose={() => setOpen(false)} landing={landing} />
      </div>
    </div>
  </motion.header>
  );
};
